import type { App } from "vue";
import type { Router } from "vue-router";
import * as apiClient from "@/apiclient";
import type { ClientSideErrorDto } from "@/apiclient";

export interface ErrorLoggerOptions {
  router?: Router;
}

type NormalizedError = {
  message: string;
  stack?: string;
};

function normalizeError(err: unknown): NormalizedError {
  if (err instanceof Error) {
    return {
      message: err.message,
      stack: err.stack,
    };
  }

  if (typeof err === "string") {
    return { message: err };
  }

  try {
    return { message: JSON.stringify(err) };
  } catch {
    return { message: String(err) };
  }
}

export default {
  install(app: App, options: ErrorLoggerOptions): void {
    if (typeof window === "undefined") return;

    let isLogging = false;

    const send = async (dto: ClientSideErrorDto) => {
      if (isLogging) return;
      isLogging = true;

      try {
        await apiClient.errorController().postApiErrorLogClientSideError(dto);
      } catch {
        // Never allow logging failures to surface
      } finally {
        isLogging = false;
      }
    };

    /* ---------------- Vue runtime errors ---------------- */
    app.config.errorHandler = (err, _instance, info) => {
      const normalized = normalizeError(err);
      send({
        errorMessage: normalized.message,
        stackTrace: normalized.stack ?? null,
        path: info,
        url: window.location.href,
      });
    };

    /* ---------------- Unhandled promise rejections ---------------- */
    window.addEventListener("unhandledrejection", (event) => {
      const normalized = normalizeError(event.reason);

      send({
        errorMessage: normalized.message,
        stackTrace: normalized.stack ?? null,
        path: "unhandledrejection",
        url: window.location.href,
      });
    });

    /* ---------------- Global JS errors ---------------- */
    window.addEventListener("error", (event) => {
      const normalized = normalizeError(event.error);

      send({
        errorMessage: event.message,
        stackTrace: normalized.stack ?? null,
        path: event.filename ?? null,
        url: window.location.href,
      });
    });

    /* ---------------- Vue Router errors ---------------- */
    if (options.router) {
      options.router.onError((error) => {
        const normalized = normalizeError(error);

        send({
          errorMessage: normalized.message,
          stackTrace: normalized.stack ?? null,
          path: "router",
          url: window.location.href,
        });
      });
    }
  },
};
